import { Component } from '@theme/component';

let measurementTableId = 0;

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  /** @type {ResizeObserver[]} */
  #tableResizeObservers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#formatTables();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#disconnectTableObservers();
    this.#formatTables();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disconnectTableObservers();
  }

  #disconnectTableObservers() {
    this.#tableResizeObservers.forEach((observer) => observer.disconnect());
    this.#tableResizeObservers = [];
  }

  #formatTables() {
    const labels = new Set();
    const tables = [...this.querySelectorAll('table')];
    tables.forEach((table, index) => this.#formatTable(table, index, tables.length, labels));
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   * @param {number} index
   * @param {number} tableCount
   * @param {Set<string>} labels
   */
  #formatTable(table, index, tableCount, labels) {
    let wrapper = table.parentElement;
    if (!wrapper?.classList.contains('rte-table-wrapper')) {
      wrapper = document.createElement('div');
      wrapper.classList.add('rte-table-wrapper');
      const parent = table.parentNode;
      if (!parent) return;
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }

    const isProductDetails = Boolean(this.closest('main[data-template*="product"] .product-details'));
    const isSizeGuide = Boolean(this.closest('[data-sc-size-guide-page]'));
    if (!isProductDetails && !isSizeGuide) return;

    let cue = wrapper.previousElementSibling;
    if (!cue?.classList.contains('sc-measurement-table-cue')) {
      measurementTableId += 1;
      cue = document.createElement('p');
      cue.classList.add('sc-measurement-table-cue');
      cue.id = `sc-measurement-table-cue-${measurementTableId}`;
      cue.textContent = 'Swipe or use arrow keys to see all measurements.';
      cue.hidden = true;
      wrapper.before(cue);
    }

    wrapper.classList.add('rte-table-wrapper--measurements');
    wrapper.setAttribute('role', 'region');
    const fallbackLabel = isSizeGuide ? 'Size guide measurements' : 'Product measurements';
    let label = this.#getTableLabel(table, wrapper, index, tableCount, fallbackLabel);
    if (labels.has(label)) label = `${label} ${index + 1}`;
    labels.add(label);
    wrapper.setAttribute('aria-label', label);

    const syncOverflowState = () => {
      const isScrollable = wrapper.scrollWidth > wrapper.clientWidth + 1;
      wrapper.classList.toggle('is-scrollable', isScrollable);
      cue.hidden = !isScrollable;

      if (isScrollable) {
        wrapper.tabIndex = 0;
        wrapper.setAttribute('aria-describedby', cue.id);
      } else {
        wrapper.removeAttribute('tabindex');
        wrapper.removeAttribute('aria-describedby');
      }
    };

    requestAnimationFrame(syncOverflowState);
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(syncOverflowState);
      observer.observe(wrapper);
      this.#tableResizeObservers.push(observer);
    }
  }

  /**
   * Uses the product copy's own caption or nearby heading for a useful,
   * distinct region name. The numbered fallback only applies to unlabelled
   * multi-table descriptions.
   * @param {HTMLTableElement} table
   * @param {HTMLElement} wrapper
   * @param {number} index
   * @param {number} tableCount
   * @param {string} fallbackLabel
   * @returns {string}
   */
  #getTableLabel(table, wrapper, index, tableCount, fallbackLabel) {
    const explicitLabel = table.getAttribute('aria-label')?.trim();
    if (explicitLabel) return explicitLabel;

    const caption = table.querySelector('caption')?.textContent?.replace(/\s+/g, ' ').trim();
    if (caption) return caption;

    let previous = wrapper.previousElementSibling;
    if (previous?.classList.contains('sc-measurement-table-cue')) previous = previous.previousElementSibling;
    while (previous) {
      const isHeading = previous.matches('h1, h2, h3, h4, h5, h6');
      const strong = previous.matches('p') ? previous.querySelector(':scope > strong, :scope > b') : null;
      const heading = (isHeading ? previous : strong)?.textContent?.replace(/\s+/g, ' ').trim();
      if (heading) return /measurement/i.test(heading) ? heading : `${heading} measurements`;
      previous = previous.previousElementSibling;
    }

    return tableCount > 1 ? `${fallbackLabel} ${index + 1}` : fallbackLabel;
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
